import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { MessageSquare, Check, X, Trash2, RefreshCw, Save } from "lucide-react";

const statusBadge = (status) => {
  if (status === "approved") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Approved · Public</Badge>;
  if (status === "rejected") return <Badge className="bg-slate-200 text-slate-700 border-slate-300">Rejected</Badge>;
  return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pending review</Badge>;
};

export default function AdminFeedback() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({}); // { id: { message, display_role, display_location } }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status_filter: statusFilter };
      const { data } = await api.get("/admin/feedback", { params });
      setItems(data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load feedback");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const beginEdit = (f) => {
    setEditing((prev) => ({
      ...prev,
      [f.id]: {
        message: f.message || "",
        display_role: f.display_role || "",
        display_location: f.display_location || "",
      },
    }));
  };

  const cancelEdit = (id) => {
    setEditing((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const saveEdit = async (id) => {
    const payload = editing[id];
    if (!payload) return;
    if ((payload.message || "").trim().length < 10) {
      toast.error("Message must be at least 10 characters.");
      return;
    }
    try {
      await api.patch(`/admin/feedback/${id}`, {
        message: payload.message.trim(),
        display_role: payload.display_role.trim(),
        display_location: payload.display_location.trim(),
      });
      toast.success("Saved");
      cancelEdit(id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/feedback/${id}`, { status });
      toast.success(status === "approved" ? "Published publicly" : `Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <Card className="border-slate-200" data-testid="admin-feedback-panel">
      <CardContent className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#111827]" />
            <h3 className="font-heading font-semibold text-lg text-[#111827]">
              Public feedback — moderation
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Show</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-44" data-testid="feedback-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending review</SelectItem>
                <SelectItem value="approved">Approved · Public</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="feedback-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-10">
            {loading ? "Loading…" : "No feedback in this view."}
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((f) => {
              const edit = editing[f.id];
              return (
                <div
                  key={f.id}
                  className="border border-slate-200 rounded-lg p-4 bg-white"
                  data-testid={`admin-feedback-${f.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="text-xs text-slate-500">
                      {new Date(f.created_at).toLocaleString()}
                      {f.submitter_email && (
                        <> · <span className="font-mono text-slate-600">{f.submitter_email}</span></>
                      )}
                      {!f.consent_public && (
                        <> · <span className="text-amber-700 font-semibold">no public consent</span></>
                      )}
                    </div>
                    {statusBadge(f.status)}
                  </div>

                  {edit ? (
                    <div className="space-y-3">
                      <Textarea
                        rows={4}
                        value={edit.message}
                        onChange={(e) =>
                          setEditing((p) => ({ ...p, [f.id]: { ...edit, message: e.target.value } }))
                        }
                        maxLength={1000}
                        data-testid={`edit-message-${f.id}`}
                      />
                      <div className="grid md:grid-cols-2 gap-3">
                        <Input
                          placeholder="Role (e.g. Medical officer)"
                          value={edit.display_role}
                          onChange={(e) =>
                            setEditing((p) => ({ ...p, [f.id]: { ...edit, display_role: e.target.value } }))
                          }
                          maxLength={80}
                          data-testid={`edit-role-${f.id}`}
                        />
                        <Input
                          placeholder="Province / setting"
                          value={edit.display_location}
                          onChange={(e) =>
                            setEditing((p) => ({ ...p, [f.id]: { ...edit, display_location: e.target.value } }))
                          }
                          maxLength={80}
                          data-testid={`edit-location-${f.id}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => saveEdit(f.id)} className="bg-[#0B5FFF] hover:bg-[#0A54E0]" data-testid={`save-${f.id}`}>
                          <Save className="w-3.5 h-3.5 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => cancelEdit(f.id)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <blockquote className="text-[#111827] italic font-heading leading-snug">
                        “{f.message}”
                      </blockquote>
                      {(f.display_role || f.display_location) && (
                        <div className="mt-2 text-xs tracking-widest uppercase text-slate-500 font-semibold">
                          {[f.display_role, f.display_location].filter(Boolean).join(" · ")}
                        </div>
                      )}
                      <div className="mt-4 flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => beginEdit(f)}
                          data-testid={`edit-${f.id}`}
                        >
                          Edit text
                        </Button>
                        {f.status !== "approved" && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(f.id, "approved")}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={!f.consent_public}
                            title={!f.consent_public ? "User did not consent to public display" : ""}
                            data-testid={`approve-${f.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Publish
                          </Button>
                        )}
                        {f.status !== "pending" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(f.id, "pending")}
                            data-testid={`repend-${f.id}`}
                          >
                            Move to pending
                          </Button>
                        )}
                        {f.status !== "rejected" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(f.id, "rejected")}
                            data-testid={`reject-${f.id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-rose-700 border-rose-200 hover:bg-rose-50"
                          onClick={() => del(f.id)}
                          data-testid={`delete-${f.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
